// Binary frames: [u32 BE header length][header JSON, UTF-8][body bytes].
// One frame per WebSocket message; bodies are opaque sealed bytes.

export type Kind = 'snapshot' | 'batch';

/** An entry as it travels: `off`/`len` into the frame body when inline, else `blob` (fetched separately). */
export interface WireEntry {
  seq: number;
  doc: string;
  kind: Kind;
  epoch: number;
  lineage: string;
  heads?: string;
  size: number;
  blob?: string;
  off?: number;
}

export type ClientMessage =
  /** `presence`: this socket wants other members' presence frames (PROTOCOL.md §10.12); older clients leave it out
   *  and are never sent one. */
  | { t: 'hello'; id: number; cursor: number; presence?: boolean }
  /** Presence (§10.12): where this device's writer is, sealed in the body; relayed, never stored, never answered.
   *  `gone` (no body): the writer left the project (closed it, went idle or hid the page). */
  | { t: 'presence'; ttl?: number; gone?: boolean }
  | {
      t: 'push' | 'snapshot';
      id: number;
      doc: string;
      epoch: number;
      lineage: string;
      cid: string;
      heads?: string;
      /** Snapshots: the cursor the client had merged when it built this snapshot. */
      base?: number;
      /** Set when the sealed bytes were uploaded to blob storage first; else they are the frame body. */
      blob?: { key: string; size: number };
      /** Fingerprint of the key the bytes are sealed with (PROTOCOL.md §10.7): after a rotation an old key's are refused. */
      keyFingerprint?: string;
    };

export type ServerMessage =
  | {
      t: 'welcome';
      id: number;
      head: number;
      next: number;
      more: boolean;
      reset?: boolean;
      /** Sizes up to which the relay keeps batches and snapshots inline: bigger ones are uploaded as blobs first. */
      limits: { inlineMax: number; snapshotInlineMax: number };
      entries: WireEntry[];
    }
  | { t: 'ack'; id: number; seq: number; dup: boolean }
  | { t: 'error'; id: number; code: string; message: string; epoch?: number; lineage?: string; /** `keyChanged`: the project's key now. */ keyFingerprint?: string }
  | { t: 'new'; entry: WireEntry }
  /** Another member's presence (§10.12), sent only to sockets that asked for it in `hello`: `from` is who the relay
   *  knows sent it (the account, a member's or the owner's, and the device); the sealed payload is the body. `gone`:
   *  that device left (or its socket closed); no body. */
  | { t: 'presence'; from: { account: string; device: string }; ttl?: number; gone?: boolean };

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export function encodeFrame(header: object, body?: Uint8Array | Uint8Array[]): Uint8Array {
  const h = encoder.encode(JSON.stringify(header));
  const parts = body === undefined ? [] : Array.isArray(body) ? body : [body];
  const total = 4 + h.length + parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  new DataView(out.buffer).setUint32(0, h.length);
  out.set(h, 4);
  let at = 4 + h.length;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
}

export function decodeFrame<T = unknown>(data: ArrayBuffer | Uint8Array): { header: T; body: Uint8Array } {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  if (bytes.length < 4) throw new Error('short frame');
  const len = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(0);
  if (4 + len > bytes.length) throw new Error('bad frame header length');
  const header = JSON.parse(decoder.decode(bytes.subarray(4, 4 + len))) as T;
  return { header, body: bytes.subarray(4 + len) };
}

// Pack responses (blob fetches through the Worker): repeated [u32 index][u32 length][bytes], in completion order.
// A missing blob has length 0xffffffff and no bytes.
export const MISSING = 0xffffffff;

export function packRecord(index: number, bytes: Uint8Array | null): Uint8Array {
  const out = new Uint8Array(8 + (bytes?.length ?? 0));
  const view = new DataView(out.buffer);
  view.setUint32(0, index);
  view.setUint32(4, bytes ? bytes.length : MISSING);
  if (bytes) out.set(bytes, 8);
  return out;
}

/** (e1, l1) outranks (e2, l2): the Swift's EpochID order, epoch then lineage. */
export function outranks(e1: number, l1: string, e2: number, l2: string): boolean {
  return e1 > e2 || (e1 === e2 && l1 > l2);
}
